import {NameListSubject, extractNarrativeNameList} from './attendanceNameList'
import {hasPackedNameLists, hasStructuredAttendanceView} from './attendanceV2ResultTable'
import {AttendanceV2Payload} from './attendanceV2Types'
import {AttendanceStatusSegment, AttendanceVisualSummary} from './buildAttendanceVisualSummary'
import {condenseAttendanceNarrative} from './condenseAttendanceNarrative'

const NAME_LIST_LABEL: Record<NonNullable<NameListSubject>, string> = {
  student: 'Students',
  teacher: 'Teachers',
  staff: 'Staff',
  class: 'Classes',
}

/** The bars the on-screen chart draws: the backend's buckets when sent, zero counts dropped. */
export function exportBarSegments(
  summary: AttendanceVisualSummary | null | undefined
): AttendanceStatusSegment[] {
  if (!summary) return []
  const source = summary.barSegments?.length ? summary.barSegments : summary.statusSegments
  return source.filter((s) => s.count > 0)
}

/** Every record the bars account for — each row lands in exactly one bucket, Checkout included. */
export function exportTotalRecords(summary: AttendanceVisualSummary | null | undefined): number {
  return exportBarSegments(summary).reduce((sum, s) => sum + s.count, 0)
}

/** The chat's markdown as plain lines for the PDF/CSV. */
export function markdownToPlainLines(markdown: string): string[] {
  return (markdown || '')
    .replace(/\s*\*\([Ss]ource[^)]*\)\*/g, '')
    .split('\n')
    .map((line) =>
      line
        .trim()
        .replace(/^#{1,6}\s+/, '')
        .replace(/^[-+*]\s+/, '• ')
        .replace(/\*\*|__|`/g, '')
        .replace(/<[^>]+>/g, '')
        .trim()
    )
    .filter(Boolean)
}

/** The reply text as the chat shows it above the card, followed by its name list and caveats. */
export function buildExportNarrativeLines(payload: AttendanceV2Payload): string[] {
  const raw = payload.kind === 'error' ? '' : payload.narrative?.trim() || ''
  const nameList = raw ? extractNarrativeNameList(raw) : null
  const headline = condenseAttendanceNarrative(nameList?.text ?? raw, {
    hasStructuredView: hasStructuredAttendanceView(payload),
  })

  const lines = markdownToPlainLines(headline)
  if (nameList && !hasPackedNameLists(payload)) {
    const label = nameList.subject ? NAME_LIST_LABEL[nameList.subject] : 'Names'
    lines.push(`${label} (${nameList.names.length}): ${nameList.names.join(', ')}`)
  }
  for (const assumption of payload.data?.assumptions ?? []) {
    const text = assumption?.trim()
    if (text) lines.push(text)
  }
  return lines
}

/** Items the backend found no records for, shown after the table as on screen. */
export function buildExportNoDataLines(payload: AttendanceV2Payload): string[] {
  const raw = payload.data?.stats?.noDataFor
  if (!Array.isArray(raw)) return []
  const names = raw.map((n) => (typeof n === 'string' ? n.trim() : '')).filter(Boolean)
  return names.length ? [`No records for (${names.length}): ${names.join(', ')}`] : []
}
